import type { UpdateProfileFormData, UserImagesFormData } from '@/core/data/auth/auth.interface';
import type { BaseClubDto } from '@/core/data/club/club.interface';
import { TabItemType } from '@/core/data/config/uiConfig.interface';
import type { ClaimedHandshakeDto, HandshakeDto, LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { StandInVerdict } from '@/core/data/handshake/handshake.type';
import type { CityDto, GeoCoords, UserGeoData } from '@/core/data/location/location.interface';
import type {
  BaseUserDto,
  BaseUserInfo,
  GetUsernameDto,
  GetUsersOptions,
  UserDto,
  ValidateUserDto,
} from '@/core/data/user/user.interface';
import { gambits } from '@/core/gambits';
import { NotificationEndpoint, UserEndpoint } from '@/core/network/api/constants/api.url.constant';
import {
  baseUserFields,
  baseUserInfoFields,
  detailUserFields,
  userInfoFields,
} from '@/core/network/api/constants/user.api_fields';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse, GetPlayerTableFilters, GetTableParams } from '@/core/network/http/httpClient.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useTableStore } from '@/stores/table.store';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { NotificationTopics } from '@/views/composables/constants/gam.constants';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

export interface UserLocationResponse {
  city: string;
  country: string;
  latitude: string;
  longitude: string;
}

const PHOTO_UPLOAD_TIMEOUT_MS = 60000;

export class UserApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  /**
   * A nearby request is narrowed to the radius around the caller, while a search
   * looks across every player. Searching therefore drops the geo parameters
   * instead of narrowing an area, and `nearMe` lets the API anchor on the
   * caller's stored location when this client has no fix of its own.
   */
  private getUsersParams(id: GamListId, options?: GetUsersOptions): GetPlayerTableFilters {
    const tableStore = useTableStore<UserDto>(id)();
    const filterStore = useFilterStore<GetPlayerTableFilters>(id)();

    const { search, ...rest } = tableStore.tableParams;

    let newSearch = search;
    if (typeof search === 'string' && search.trim().length === 0) {
      newSearch = undefined;
    }

    const params: GetPlayerTableFilters = {
      search: newSearch,
      ...rest,
      ...filterStore.getFilters(),
      fields: baseUserFields.join(','),
      relationship: filterStore.getActiveTab(),
    };

    if (!options?.nearby || newSearch) return params;

    return {
      ...params,
      nearMe: true,
      latitude: options.location?.lat || undefined,
      longitude: options.location?.lng || undefined,
      distanceLte: gambits.configService.getNearbyRadius(),
    };
  }

  async getLocationFromCountry(): Promise<AxiosResponse<GamResponse<UserLocationResponse>>> {
    return this.httpClient.get<any>(UserEndpoint.GET_USER_LOCATION_FROM_COUNTRY);
  }

  async getIpLocation(): Promise<AxiosResponse<UserLocationResponse>> {
    return axios.get<UserLocationResponse>('https://api.gambits.com/cflocate');
  }

  async updateLocation(geoData: UserGeoData): Promise<AxiosResponse<GamResponse<UserGeoData>>> {
    return await this.httpClient.put(UserEndpoint.UPDATE_LOCATION, {
      data: geoData,
    });
  }

  async updateCity(coordinates: GeoCoords): Promise<AxiosResponse<GamResponse<CityDto>>> {
    return await this.httpClient.put(UserEndpoint.UPDATE_CITY, {
      data: coordinates,
    });
  }

  async getUsers(listId: GamListId, options?: GetUsersOptions): Promise<AxiosResponse<GamResponse<BaseUserDto[]>>> {
    return this.httpClient.get<BaseUserDto[], GetPlayerTableFilters>(UserEndpoint.USERS, {
      config: {
        params: this.getUsersParams(listId, options),
      },
    });
  }

  async getFollowersByQuery(query: string): Promise<AxiosResponse<GamResponse<BaseUserDto[]>>> {
    return this.httpClient.get<BaseUserDto[], GetPlayerTableFilters>(UserEndpoint.USERS, {
      config: {
        params: {
          fields: baseUserFields.join(','),
          search: query,
          relationship: TabItemType.LOCATIONS,
        },
      },
    });
  }

  async getFollowers(): Promise<AxiosResponse<GamResponse<BaseUserDto[]>>> {
    return this.httpClient.get<BaseUserDto[], GetPlayerTableFilters>(UserEndpoint.USERS, {
      config: {
        params: {
          fields: baseUserFields.join(','),
          relationship: TabItemType.LOCATIONS,
        },
      },
    });
  }

  async getFollowing(): Promise<AxiosResponse<GamResponse<BaseUserDto[]>>> {
    return this.httpClient.get<BaseUserDto[], GetPlayerTableFilters>(UserEndpoint.USERS, {
      config: {
        params: {
          fields: baseUserFields.join(','),
          relationship: TabItemType.FOLLOWING,
        },
      },
    });
  }

  async getRecentInvitees(): Promise<AxiosResponse<GamResponse<BaseUserDto[]>>> {
    return this.httpClient.get<BaseUserDto[]>(UserEndpoint.RECENT_INVITEES);
  }

  async getUser(id: string): Promise<AxiosResponse<GamResponse<UserDto>>> {
    return this.httpClient.get<UserDto, GetTableParams>(UserEndpoint.GET_USER_DETAILS, {
      urlParams: {
        id,
      },
      config: {
        params: {
          fields: detailUserFields.join(','),
        },
      },
    });
  }

  async getUserByName(username: string): Promise<AxiosResponse<GamResponse<UserDto>>> {
    return this.httpClient.get<UserDto, GetTableParams>(UserEndpoint.GET_USER_DETAILS_BY_NAME, {
      config: {
        params: {
          fields: detailUserFields.join(','),
        },
      },
      urlParams: { username },
    });
  }

  /**
   * Everyone's pairs, not this user's. It is left out of the profile payload so
   * nobody waits on it to see their own page, and asked for only when the sheet
   * that shows it is opened.
   */
  async getWorldHandshakePairs(
    userId: string,
  ): Promise<AxiosResponse<GamResponse<{ worldUniqueHandshakePairs: number }>>> {
    return this.httpClient.get<{ worldUniqueHandshakePairs: number }, GetTableParams>(UserEndpoint.GET_USER_DETAILS, {
      urlParams: { id: userId },
      config: {
        params: {
          fields: 'worldUniqueHandshakePairs',
        },
      },
    });
  }

  async getHandshakePartners(userId: string): Promise<AxiosResponse<GamResponse<{ handshakes: HandshakeDto[] }>>> {
    return this.httpClient.get<{ handshakes: HandshakeDto[] }, GetTableParams>(UserEndpoint.GET_USER_DETAILS, {
      urlParams: { id: userId },
      config: {
        params: {
          fields: 'handshakes',
        },
      },
    });
  }

  async getUserInfo(): Promise<AxiosResponse<GamResponse<BaseUserInfo>>> {
    return this.httpClient.get<BaseUserInfo>(UserEndpoint.GET_USER_INFO, {
      config: {
        params: {
          fields: userInfoFields.join(','),
        },
      },
    });
  }

  async getUserClubs(): Promise<AxiosResponse<GamResponse<BaseClubDto[]>>> {
    return this.httpClient.get<BaseClubDto[]>(UserEndpoint.GET_USER_CLUBS, {
      config: {
        params: {
          fields: baseUserInfoFields.join(','),
        },
      },
    });
  }

  async updateProfile(profile: UpdateProfileFormData): Promise<AxiosResponse<GamResponse<BaseUserInfo>>> {
    return await this.httpClient.put(UserEndpoint.USERS, {
      data: profile,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async updateNotificationPreferences(
    notificationPreferences: import('@/core/data/user/user.interface').NotificationPreferences,
  ): Promise<AxiosResponse<GamResponse<import('@/core/data/user/user.interface').NotificationPreferences>>> {
    return await this.httpClient.put(UserEndpoint.GET_USER_NOTIFICATION_PREFS, {
      data: notificationPreferences,
    });
  }

  async updateUnitTypePreferences(unitType: string): Promise<AxiosResponse<GamResponse<string>>> {
    return await this.httpClient.put(UserEndpoint.GET_USER_UNIT_TYPE, {
      data: { unitType },
    });
  }

  async markActive(): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.post(UserEndpoint.MARK_ACTIVE, {});
  }

  async updateAccentHue(accentHue: number | null): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(UserEndpoint.UPDATE_USER_ACCENT_HUE, {
      data: { accentHue },
    });
  }

  async updateRecentClockControls(
    controls: { minutes: number; increment: number }[],
  ): Promise<AxiosResponse<GamResponse<{ minutes: number; increment: number }[]>>> {
    return await this.httpClient.put(UserEndpoint.UPDATE_RECENT_CLOCK_CONTROLS, {
      data: { controls },
    });
  }

  async updateEmoji(emoji: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(UserEndpoint.USERS, {
      data: { emoji },
    });
  }

  async updateImages(profile: UserImagesFormData): Promise<AxiosResponse<GamResponse<BaseUserInfo>>> {
    return await this.httpClient.put(UserEndpoint.UPDATE_IMAGES, {
      data: profile,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async getUsername(username: string): Promise<AxiosResponse<GamResponse<GetUsernameDto>>> {
    return this.httpClient.get<GetUsernameDto>(UserEndpoint.CHECK_USERNAME, {
      urlParams: {
        username,
      },
    });
  }

  async validateUsername(username: string): Promise<AxiosResponse<GamResponse<ValidateUserDto>>> {
    return this.httpClient.get<ValidateUserDto>(UserEndpoint.VALIDATE_USERNAME, {
      config: {
        params: {
          username,
        },
      },
    });
  }

  async storeNotificationToken(
    token: string,
    topics: NotificationTopics[] = [],
  ): Promise<AxiosResponse<GamResponse<boolean>>> {
    return await this.httpClient.put(NotificationEndpoint.NOTIFICATION_TOKEN, {
      data: { token, enabledNotifications: topics },
    });
  }

  async deactivateNotificationToken(token: string): Promise<AxiosResponse<GamResponse<unknown>>> {
    return await this.httpClient.put(NotificationEndpoint.NOTIFICATION_TOKEN_BY_TOKEN, {
      data: { active: false },
      urlParams: { token: encodeURIComponent(token) },
    });
  }

  async deactivateAllNotificationTokens(): Promise<AxiosResponse<GamResponse<unknown>>> {
    return await this.httpClient.post(NotificationEndpoint.NOTIFICATION_TOKEN_DEACTIVATE_ALL, {
      data: {},
    });
  }

  async toggleFollow(id: string, isFollowing?: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.post(isFollowing ? UserEndpoint.UNFOLLOW_USER : UserEndpoint.FOLLOW_USER, {
      urlParams: { id },
    });
  }

  async toggleBlock(id: string, isBlocked?: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.post(isBlocked ? UserEndpoint.UNBLOCK_USER : UserEndpoint.BLOCK_USER, {
      urlParams: { id },
    });
  }

  async generateHandshakeCode(): Promise<AxiosResponse<GamResponse<{ code: string; expiresAt: string }>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_CODE, {});
  }

  async resolveHandshakeCode(
    code: string,
  ): Promise<AxiosResponse<GamResponse<{ userId: string; username: string; token: string }>>> {
    return await this.httpClient.get(UserEndpoint.HANDSHAKE_CODE_RESOLVE, { urlParams: { code } });
  }

  async offerHandshake(
    playerId: string,
    image: File | null | string | undefined,
    options: {
      gambitId?: string;
      locationId?: string;
      token?: string;
      note?: string;
      suggestionSeed?: string;
      suggestionRound?: number;
    } = {},
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    const url = `${UserEndpoint.HANDSHAKES}?playerId=${playerId}`;

    const { suggestionRound, ...rest } = options;

    return await this.httpClient.post(url, {
      data: { handshakeImage: image },
      queryParams: {
        ...rest,
        ...(suggestionRound != null ? { suggestionRound: String(suggestionRound) } : {}),
      },
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async createPasserbyHandshake(
    options: { suggestionSeed?: string; suggestionRound?: number } = {},
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    const { suggestionRound, ...rest } = options;

    return await this.httpClient.post(UserEndpoint.HANDSHAKE_PASSERBY, {
      queryParams: {
        ...rest,
        ...(suggestionRound != null ? { suggestionRound: String(suggestionRound) } : {}),
      },
    });
  }

  async claimPasserbyHandshake(code: string): Promise<AxiosResponse<GamResponse<ClaimedHandshakeDto>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_PASSERBY_CLAIM, { data: { code } });
  }

  async acceptHandshake(
    handshakeId: string,
    image: File | null | string | undefined,
    note?: string,
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.put(UserEndpoint.HANDSHAKE, {
      urlParams: { id: handshakeId },
      data: { handshakeImage: image },
      queryParams: note ? { note } : undefined,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async getLiveHandshakes(): Promise<AxiosResponse<GamResponse<LiveHandshakeDto[]>>> {
    return await this.httpClient.get(UserEndpoint.HANDSHAKES_LIVE);
  }

  async closeHandshake(handshakeId: string): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_CLOSE, {
      urlParams: { id: handshakeId },
    });
  }

  async deleteHandshake(handshakeId: string): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.delete(UserEndpoint.HANDSHAKE, {
      urlParams: { id: handshakeId },
    });
  }

  async addHandshakeImage(
    handshakeId: string,
    image: File,
    signal?: AbortSignal,
    positionIndex?: number,
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_ADD_IMAGE, {
      urlParams: { id: handshakeId },
      ...(positionIndex != null ? { queryParams: { position: String(positionIndex) } } : {}),
      data: { handshakeImage: image },
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal,
        // A photo on a thin connection needs longer than the deadline the rest
        // of the app runs on, and giving up early only means the photo shows up
        // later than it landed.
        timeout: PHOTO_UPLOAD_TIMEOUT_MS,
      },
    });
  }

  async removeHandshakeImage(handshakeId: string, imageId: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(UserEndpoint.HANDSHAKE_IMAGE, {
      urlParams: { id: handshakeId, imageId: imageId },
    });
  }

  async moveHandshakeImage(
    handshakeId: string,
    imageId: string,
    positionIndex: number | null,
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.put(UserEndpoint.HANDSHAKE_IMAGE, {
      urlParams: { id: handshakeId, imageId },
      data: { positionIndex },
    });
  }

  async setHandshakeNote(handshakeId: string, note: string): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.put(UserEndpoint.HANDSHAKE_NOTE, {
      urlParams: { id: handshakeId },
      data: { note },
    });
  }

  async setStandInVerdict(
    handshakeId: string,
    verdict: StandInVerdict,
    name?: string,
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.put(UserEndpoint.HANDSHAKE_STAND_IN, {
      urlParams: { id: handshakeId },
      data: { verdict, ...(name ? { name } : {}) },
    });
  }

  async nextHandshakeSuggestion(
    handshakeId: string,
    options: { keepColours?: boolean } = {},
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_SUGGESTION, {
      urlParams: { id: handshakeId },
      data: options,
    });
  }

  async updateHandshakeSuggestion(
    handshakeId: string,
    change: { swapColours?: boolean; minutes?: number; increment?: number },
  ): Promise<AxiosResponse<GamResponse<HandshakeDto>>> {
    return await this.httpClient.put(UserEndpoint.HANDSHAKE_SUGGESTION, {
      urlParams: { id: handshakeId },
      data: change,
    });
  }

  async dealHandshakePosition(
    seed: string,
    round: number,
  ): Promise<AxiosResponse<GamResponse<{ positionIndex: number }>>> {
    return await this.httpClient.get(UserEndpoint.HANDSHAKE_DEAL, {
      queryParams: { seed, round: String(round) },
    });
  }

  async getHandshakePositions(handshakeId: string): Promise<
    AxiosResponse<
      GamResponse<{
        positions: number[];
        openPositions?: number[];
        declinedPositions?: number[];
        suggestionPositions: number[] | null;
      }>
    >
  > {
    return await this.httpClient.get(UserEndpoint.HANDSHAKE_POSITIONS, {
      urlParams: { id: handshakeId },
    });
  }

  async declineHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<AxiosResponse<GamResponse<{ declinedPositions: number[] }>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_DECLINED_POSITION, {
      urlParams: { id: handshakeId },
      data: { positionIndex },
    });
  }

  async startHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<AxiosResponse<GamResponse<{ startedPositions: number[] }>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_STARTED_POSITION, {
      urlParams: { id: handshakeId },
      data: { positionIndex },
    });
  }

  async checkHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<AxiosResponse<GamResponse<{ positions: number[] }>>> {
    return await this.httpClient.post(UserEndpoint.HANDSHAKE_POSITION, {
      urlParams: { id: handshakeId },
      data: { positionIndex },
    });
  }

  async uncheckHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<AxiosResponse<GamResponse<{ positions: number[] }>>> {
    return await this.httpClient.delete(UserEndpoint.HANDSHAKE_POSITION_INDEX, {
      urlParams: { id: handshakeId, positionIndex: String(positionIndex) },
    });
  }
}
